/**
 * CustomARCForm — multi-category ARC request wizard
 *
 * Step 0 – Category Selection  : add one or more cascading categories
 * Step 1 – Details & Files     : description + uploads per category, shared contractor checkbox
 * Step 2 – Review & Acknowledge: read-only summary + acknowledgments → submit
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Typography, Button, IconButton, Card, FormControl,
  InputLabel, Select, MenuItem, TextField, Grid, Collapse,
  Chip, CircularProgress, Divider, Checkbox, FormControlLabel,
  Stepper, Step, StepLabel, Alert, Accordion, AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useDropzone } from "react-dropzone";
import api from "../../utils/api";

// ─── helpers ─────────────────────────────────────────────────────────────────
let _uid = 0;
const uid = () => ++_uid;

const BLANK_CONTRACTOR = { contractor_name: "", phone_number: "", company_name: "", notes: "" };

const imageAccept   = { "image/*": [] };
const allDocsAccept = {
  "image/*": [], "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
};

// ─── Upload drop zone ─────────────────────────────────────────────────────────
function DropZone({ label, required, files, onDrop, onRemove, accept }) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept, multiple: true });
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
        {label}{required && <span style={{ color: "#d32f2f" }}> *</span>}
      </Typography>
      <Box {...getRootProps()} sx={{
        p: 2, border: files.length === 0 && required ? "2px dashed rgba(211,47,47,0.35)" : "2px dashed rgba(47,99,173,0.22)",
        borderRadius: "12px", textAlign: "center", cursor: "pointer",
        bgcolor: isDragActive ? "rgba(47,99,173,0.07)" : "rgba(47,99,173,0.02)",
        "&:hover": { bgcolor: "#f5faff", borderColor: "#1976d2" },
      }}>
        <input {...getInputProps()} />
        <CloudUploadIcon sx={{ fontSize: 28, color: files.length > 0 ? "#2e7d32" : "#1976d2", mb: 0.25 }} />
        <Typography variant="body2" sx={{ color: "#223142", fontWeight: 600 }}>
          {isDragActive ? "Drop here…" : "Drag & drop or click to select"}
        </Typography>
      </Box>
      {files.length > 0 && (
        <Box sx={{ mt: 0.75, border: "1px solid rgba(47,99,173,0.08)", borderRadius: "10px", p: 0.5, bgcolor: "#fff" }}>
          {files.map((f, i) => (
            <Box key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.3, px: 0.5 }}>
              <Typography variant="caption" sx={{ maxWidth: "85%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {f.name}
              </Typography>
              <IconButton size="small" color="error" onClick={() => onRemove(i)}>
                <DeleteIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ─── Cascade picker for one entry ────────────────────────────────────────────
function CascadePicker({ cascade, onChange }) {
  return (
    <Box>
      {cascade.map((level, idx) => (
        <FormControl fullWidth key={idx} size="small" sx={{ mb: 1 }}>
          <InputLabel>{idx === 0 ? "Category" : `Sub-category${idx > 1 ? ` (level ${idx + 1})` : ""}`}</InputLabel>
          <Select
            value={level.selected || ""}
            label={idx === 0 ? "Category" : `Sub-category${idx > 1 ? ` (level ${idx + 1})` : ""}`}
            onChange={e => onChange(idx, e.target.value || null)}
          >
            <MenuItem value=""><em>Select…</em></MenuItem>
            {level.options.map(o => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
          </Select>
        </FormControl>
      ))}
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function CustomARCForm({ propertyId, accountId, onClose, acknowledgments, infoSections }) {
  const [step, setStep] = useState(0);

  // Root options (loaded once)
  const [rootOptions, setRootOptions] = useState([]);

  // List of category entries
  // Each: { _key, cascade: [{options, selected}], arc_category_id, label,
  //         description, comment, start_date, end_date,
  //         frontPhotos, impactPhotos, supportingDocs }
  const [entries, setEntries] = useState([]);

  // Contractor
  const [sameContractor, setSameContractor] = useState(true);
  const [sharedContractor, setSharedContractor] = useState(BLANK_CONTRACTOR);
  const [perContractor, setPerContractor] = useState({}); // key → contractor obj

  // Acknowledgments
  const [checked, setChecked] = useState({});

  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });
  const [currentUserId, setCurrentUserId] = useState(null);

  // Contractor visibility per entry
  const [showContractor, setShowContractor] = useState({}); // key → bool

  useEffect(() => {
    const u = sessionStorage.getItem("user");
    if (u) { try { setCurrentUserId(JSON.parse(u)?.userId ?? null); } catch {} }
  }, []);

  useEffect(() => {
    api.get("/arc-custom-categories/children/root")
      .then(r => {
        setRootOptions(r.data || []);
        // Initialise with one blank entry
        setEntries([makeEntry(r.data || [])]);
      })
      .catch(() => {});
  }, []);

  function makeEntry(roots) {
    return {
      _key: uid(),
      cascade: [{ options: roots, selected: null }],
      arc_category_id: null,
      label: "",
      description: "",
      comment: "",
      start_date: "",
      end_date: "",
      frontPhotos: [],
      impactPhotos: [],
      supportingDocs: [],
    };
  }

  // ── Cascade change ────────────────────────────────────────────────────────
  const handleCascadeChange = async (entryKey, levelIndex, selectedId) => {
    setEntries(prev => prev.map(e => {
      if (e._key !== entryKey) return e;
      const updated = e.cascade.slice(0, levelIndex + 1).map((lvl, i) =>
        i === levelIndex ? { ...lvl, selected: selectedId } : lvl
      );
      return { ...e, cascade: updated, arc_category_id: null, label: "" };
    }));

    if (!selectedId) return;

    // Load children
    try {
      const res = await api.get(`/arc-custom-categories/children/${selectedId}`);
      setEntries(prev => prev.map(e => {
        if (e._key !== entryKey) return e;
        const base = e.cascade.slice(0, levelIndex + 1).map((lvl, i) =>
          i === levelIndex ? { ...lvl, selected: selectedId } : lvl
        );
        if (res.data && res.data.length > 0) {
          base.push({ options: res.data, selected: null });
        }
        // Build label from all selected names
        const label = base
          .filter(l => l.selected)
          .map(l => l.options.find(o => o.id === l.selected)?.name)
          .filter(Boolean)
          .join(" › ");
        // The deepest selected id is the arc_category_id
        const deepest = base.filter(l => l.selected).slice(-1)[0]?.selected;
        const hasChildren = res.data && res.data.length > 0;
        return { ...e, cascade: base, arc_category_id: hasChildren ? null : (deepest || null), label };
      }));
    } catch {
      setEntries(prev => prev.map(e => {
        if (e._key !== entryKey) return e;
        const base = e.cascade.slice(0, levelIndex + 1).map((lvl, i) =>
          i === levelIndex ? { ...lvl, selected: selectedId } : lvl
        );
        const label = base.filter(l => l.selected).map(l => l.options.find(o => o.id === l.selected)?.name).filter(Boolean).join(" › ");
        const deepest = base.filter(l => l.selected).slice(-1)[0]?.selected;
        return { ...e, cascade: base, arc_category_id: deepest || null, label };
      }));
    }
  };

  // After cascade completes (no more children), ensure arc_category_id is set
  // The logic above covers it — leaf is set when children response is empty.

  const addEntry = () => setEntries(prev => [...prev, makeEntry(rootOptions)]);
  const removeEntry = (key) => setEntries(prev => prev.filter(e => e._key !== key));

  const updateEntry = (key, field, value) =>
    setEntries(prev => prev.map(e => e._key === key ? { ...e, [field]: value } : e));

  const addFiles = (key, field, files) =>
    setEntries(prev => prev.map(e => e._key === key ? { ...e, [field]: [...e[field], ...files] } : e));

  const removeFile = (key, field, idx) =>
    setEntries(prev => prev.map(e => e._key === key ? { ...e, [field]: e[field].filter((_, i) => i !== idx) } : e));

  // ── Validation ────────────────────────────────────────────────────────────
  const step0Valid = entries.length > 0 && entries.every(e => e.arc_category_id || e.cascade.some(l => l.selected));
  const step1Valid = entries.every(e => e.description.trim() && e.frontPhotos.length > 0 && e.impactPhotos.length > 0);
  const allAcked = acknowledgments.length === 0 || acknowledgments.every((a, i) => !!checked[a.id ?? i]);

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!allAcked) {
      setSnack({ open: true, msg: "Please acknowledge all statements before submitting.", severity: "warning" });
      return;
    }
    setSubmitting(true);
    try {
      // Create a batch record to group all requests together
      let batchId = null;
      if (entries.length > 1) {
        const batchRes = await api.post("/arc-request-batches", {
          property_id: propertyId,
          account_id: accountId,
        });
        batchId = batchRes.data?.id || null;
      }

      for (const entry of entries) {
        // Resolve the deepest selected category id
        const catId = entry.arc_category_id
          || entry.cascade.filter(l => l.selected).slice(-1)[0]?.selected
          || null;

        const res = await api.post("/architectural-requests", {
          property_id: propertyId,
          account_id: accountId,
          request_type: null,
          arc_category_id: catId,
          batch_id: batchId,
          description: entry.description,
          comment: entry.comment,
          anticipated_start_date: entry.start_date || null,
          anticipated_completion_date: entry.end_date || null,
          acknowledged: true,
          created_by: currentUserId,
        });
        const request_id = res.data?.id;
        if (!request_id) continue;

        // Upload files
        const allFiles = [...entry.frontPhotos, ...entry.impactPhotos, ...entry.supportingDocs];
        if (allFiles.length > 0) {
          const fd = new FormData();
          fd.append("request_id", request_id);
          allFiles.forEach(f => fd.append("upload_urls", f));
          await api.post("/request-files", fd, { headers: { "Content-Type": "multipart/form-data" } });
        }

        // Contractor
        const ctr = sameContractor ? sharedContractor : (perContractor[entry._key] || BLANK_CONTRACTOR);
        const showC = sameContractor ? Object.values(sharedContractor).some(v => String(v || "").trim()) : showContractor[entry._key];
        if (showC && Object.values(ctr).some(v => String(v || "").trim())) {
          await api.post("/architectural-request-contractors", {
            architectural_request_id: request_id,
            contractor_name: ctr.contractor_name?.trim() || "",
            phone_number: ctr.phone_number?.trim() || "",
            company_name: ctr.company_name?.trim() || "",
            notes: ctr.notes?.trim() || "",
          }).catch(() => {});
        }
      }

      setSnack({ open: true, msg: `${entries.length} request${entries.length > 1 ? "s" : ""} submitted successfully!`, severity: "success" });
      setTimeout(() => onClose?.(), 1800);
    } catch (err) {
      setSnack({ open: true, msg: "Submission failed: " + err.message, severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Contractor form ───────────────────────────────────────────────────────
  const ContractorFields = ({ value, onChange }) => (
    <Grid container spacing={2} sx={{ mt: 0.5 }}>
      {[["contractor_name", "Contractor Name"], ["company_name", "Company Name"], ["phone_number", "Phone"], ["notes", "Notes"]].map(([field, label]) => (
        <Grid item xs={12} sm={field === "notes" ? 12 : 6} key={field}>
          <TextField fullWidth size="small" label={label} value={value[field] || ""}
            onChange={e => onChange({ ...value, [field]: e.target.value })}
            multiline={field === "notes"} rows={field === "notes" ? 2 : 1} />
        </Grid>
      ))}
    </Grid>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // STEP 0 — Category selection
  // ─────────────────────────────────────────────────────────────────────────
  const renderStep0 = () => (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>Select Categories</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Add one or more categories for this request. You can add as many as needed.
      </Typography>

      {infoSections?.map(sec => (
        <Box key={sec.id} sx={{ p: 2, mb: 2, borderRadius: "12px", bgcolor: sec.bg_color, border: `1px solid ${sec.border_color}` }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{sec.title}</Typography>
          <Typography variant="body2" color="text.secondary">{sec.content}</Typography>
        </Box>
      ))}

      {entries.map((entry, idx) => (
        <Card key={entry._key} sx={{ mb: 2, borderRadius: "14px", border: "1px solid rgba(47,99,173,0.12)", boxShadow: "none" }}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip label={idx + 1} size="small" color="primary" sx={{ fontWeight: 700, width: 28, height: 28, borderRadius: "50%" }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142" }}>
                  {entry.label || "Select a category"}
                </Typography>
              </Box>
              {entries.length > 1 && (
                <IconButton size="small" color="error" onClick={() => removeEntry(entry._key)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            <CascadePicker
              cascade={entry.cascade}
              onChange={(lvl, val) => handleCascadeChange(entry._key, lvl, val)}
            />

            {entry.label && (
              <Box sx={{ mt: 0.5, p: 1, borderRadius: "8px", bgcolor: "rgba(47,99,173,0.05)", display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#2e7d32" }} />
                <Typography variant="caption" sx={{ color: "#223142", fontWeight: 600 }}>{entry.label}</Typography>
              </Box>
            )}
          </Box>
        </Card>
      ))}

      <Button startIcon={<AddCircleOutlineIcon />} onClick={addEntry} variant="outlined"
        sx={{ borderRadius: "10px", textTransform: "none", borderStyle: "dashed", fontWeight: 600 }}>
        Add Another Category
      </Button>
    </Box>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // STEP 1 — Details & Files per category
  // ─────────────────────────────────────────────────────────────────────────
  const renderStep1 = () => (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>Details & Files</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Fill in the details and upload required photos for each selected category.
      </Typography>

      {entries.map((entry, idx) => (
        <Accordion key={entry._key} defaultExpanded={idx === 0}
          sx={{ mb: 1.5, borderRadius: "14px !important", border: "1px solid rgba(47,99,173,0.12)", boxShadow: "none", "&:before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ borderRadius: "14px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Chip label={idx + 1} size="small" color="primary" sx={{ fontWeight: 700, width: 26, height: 26, borderRadius: "50%", fontSize: 12 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142" }}>
                {entry.label || `Category ${idx + 1}`}
              </Typography>
              {entry.description && entry.frontPhotos.length > 0 && entry.impactPhotos.length > 0 && (
                <Chip label="Complete" size="small" color="success" sx={{ height: 20, fontSize: 11 }} />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <TextField fullWidth required label="Description" multiline rows={3} value={entry.description}
              onChange={e => updateEntry(entry._key, "description", e.target.value)}
              sx={{ mb: 2 }} />

            <TextField fullWidth label="Comment (optional)" multiline rows={2} value={entry.comment}
              onChange={e => updateEntry(entry._key, "comment", e.target.value)}
              sx={{ mb: 2 }} />

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth type="date" label="Anticipated Start Date"
                  InputLabelProps={{ shrink: true }} value={entry.start_date}
                  onChange={e => updateEntry(entry._key, "start_date", e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth type="date" label="Anticipated Completion Date"
                  InputLabelProps={{ shrink: true }} value={entry.end_date}
                  onChange={e => updateEntry(entry._key, "end_date", e.target.value)} />
              </Grid>
            </Grid>

            <DropZone label="Color Photo of Front of Property" required
              files={entry.frontPhotos} accept={imageAccept}
              onDrop={files => addFiles(entry._key, "frontPhotos", files)}
              onRemove={i => removeFile(entry._key, "frontPhotos", i)} />

            <DropZone label="Color Photo of Impact Area" required
              files={entry.impactPhotos} accept={imageAccept}
              onDrop={files => addFiles(entry._key, "impactPhotos", files)}
              onRemove={i => removeFile(entry._key, "impactPhotos", i)} />

            <DropZone label="Supporting Documents (optional)"
              files={entry.supportingDocs} accept={allDocsAccept}
              onDrop={files => addFiles(entry._key, "supportingDocs", files)}
              onRemove={i => removeFile(entry._key, "supportingDocs", i)} />

            {/* Per-category contractor toggle (only shown when sameContractor is OFF) */}
            {!sameContractor && (
              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  control={<Checkbox checked={!!showContractor[entry._key]}
                    onChange={e => setShowContractor(p => ({ ...p, [entry._key]: e.target.checked }))} />}
                  label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Add Contractor Info for this category</Typography>}
                />
                <Collapse in={!!showContractor[entry._key]}>
                  <ContractorFields
                    value={perContractor[entry._key] || BLANK_CONTRACTOR}
                    onChange={val => setPerContractor(p => ({ ...p, [entry._key]: val }))} />
                </Collapse>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Shared contractor section */}
      <Card sx={{ mt: 2, p: 2, borderRadius: "14px", border: "1px solid rgba(47,99,173,0.12)", boxShadow: "none" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142", mb: 1 }}>Contractor Information</Typography>
        <FormControlLabel
          control={<Checkbox checked={sameContractor} onChange={e => setSameContractor(e.target.checked)} />}
          label={<Typography variant="body2">Same contractor for all categories</Typography>}
        />
        <Collapse in={sameContractor}>
          <ContractorFields value={sharedContractor} onChange={setSharedContractor} />
        </Collapse>
        {!sameContractor && (
          <Typography variant="caption" color="text.secondary">
            Use the "Add Contractor Info" toggle inside each category above to set per-category contractor details.
          </Typography>
        )}
      </Card>
    </Box>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // STEP 2 — Review & Acknowledge
  // ─────────────────────────────────────────────────────────────────────────
  const renderStep2 = () => (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>Review & Submit</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Review your request details below, then acknowledge the terms and submit.
      </Typography>

      {entries.map((entry, idx) => (
        <Card key={entry._key} sx={{ mb: 2, borderRadius: "14px", border: "1px solid rgba(47,99,173,0.12)", boxShadow: "none" }}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <Chip label={idx + 1} size="small" color="primary" sx={{ fontWeight: 700, width: 26, height: 26, borderRadius: "50%", fontSize: 12 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142" }}>{entry.label || `Category ${idx + 1}`}</Typography>
            </Box>

            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Description</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{entry.description || "—"}</Typography>
              </Grid>
              {entry.comment && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Comment</Typography>
                  <Typography variant="body2">{entry.comment}</Typography>
                </Grid>
              )}
              {(entry.start_date || entry.end_date) && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Dates</Typography>
                  <Typography variant="body2">{entry.start_date || "—"} → {entry.end_date || "—"}</Typography>
                </Grid>
              )}
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Front Photos</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{entry.frontPhotos.length} file{entry.frontPhotos.length !== 1 ? "s" : ""}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Impact Photos</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{entry.impactPhotos.length} file{entry.impactPhotos.length !== 1 ? "s" : ""}</Typography>
              </Grid>
              {entry.supportingDocs.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Supporting Docs</Typography>
                  <Typography variant="body2">{entry.supportingDocs.length} file{entry.supportingDocs.length !== 1 ? "s" : ""}</Typography>
                </Grid>
              )}

              {/* Contractor for this entry */}
              {(() => {
                const ctr = sameContractor ? sharedContractor : (perContractor[entry._key] || BLANK_CONTRACTOR);
                const hasData = Object.values(ctr).some(v => String(v || "").trim());
                const shown = sameContractor ? hasData : (showContractor[entry._key] && hasData);
                if (!shown) return null;
                return (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 0.5 }} />
                    <Typography variant="caption" color="text.secondary">Contractor</Typography>
                    <Typography variant="body2">{[ctr.contractor_name, ctr.company_name, ctr.phone_number].filter(Boolean).join(" · ")}</Typography>
                  </Grid>
                );
              })()}
            </Grid>
          </Box>
        </Card>
      ))}

      {/* Acknowledgments */}
      {acknowledgments.length > 0 && (
        <Card sx={{ p: 2, borderRadius: "14px", border: "1px solid rgba(47,99,173,0.12)", boxShadow: "none" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142", mb: 1.5 }}>
            Acknowledgments
          </Typography>
          {acknowledgments.map((ack, i) => (
            <FormControlLabel key={ack.id ?? i} sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}
              control={
                <Checkbox size="small" checked={!!checked[ack.id ?? i]}
                  onChange={e => setChecked(p => ({ ...p, [ack.id ?? i]: e.target.checked }))}
                  sx={{ mt: -0.25 }} />
              }
              label={<Typography variant="body2">{ack.bullet_text}</Typography>}
            />
          ))}
        </Card>
      )}

      {snack.open && (
        <Alert severity={snack.severity} sx={{ mt: 2, borderRadius: 2 }} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      )}
    </Box>
  );

  // ─────────────────────────────────────────────────────────────────────────
  const steps = ["Select Categories", "Details & Files", "Review & Submit"];
  const canNext = step === 0 ? step0Valid : step === 1 ? step1Valid : allAcked;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stepper activeStep={step} sx={{ mb: 3 }}>
        {steps.map(label => (
          <Step key={label}>
            <StepLabel sx={{ "& .MuiStepLabel-label": { fontWeight: 600, fontSize: 13 } }}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ minHeight: 300 }}>
        {step === 0 && renderStep0()}
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3, pt: 2, borderTop: "1px solid rgba(47,99,173,0.08)" }}>
        <Button variant="outlined" onClick={step === 0 ? onClose : () => setStep(s => s - 1)}
          startIcon={<ArrowBackIcon />} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}>
          {step === 0 ? "Cancel" : "Back"}
        </Button>

        {step < 2 ? (
          <Button variant="contained" disabled={!canNext} onClick={() => setStep(s => s + 1)}
            endIcon={<ArrowForwardIcon />} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}>
            Next
          </Button>
        ) : (
          <Button variant="contained" color="success" disabled={submitting || !allAcked} onClick={handleSubmit}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, minWidth: 130 }}>
            {submitting ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Submit Request"}
          </Button>
        )}
      </Box>
    </Box>
  );
}
